import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Loader2, Settings, Key, Globe, MessageSquare, Save, Eye, EyeOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminSettingsPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Website settings
  const [settings, setSettings] = useState({
    site_name: '',
    tagline: '',
    hero_title: '',
    hero_subtitle: '',
    contact_email: ''
  });

  // API Credentials
  const [credentials, setCredentials] = useState({
    paypal_client_id: '',
    paypal_secret: '',
    sendgrid_api_key: '',
    sendgrid_sender_email: '',
    twitter_api_key: '',
    twitter_api_secret: '',
    twitter_access_token: '',
    twitter_access_secret: '',
    facebook_app_id: '',
    facebook_app_secret: '',
    facebook_page_token: '',
    instagram_access_token: '',
    tiktok_access_token: '',
    whatsapp_phone_id: '',
    whatsapp_access_token: ''
  });

  const [showSecrets, setShowSecrets] = useState({});

  const getHeaders = () => ({ Authorization: `Bearer ${token}` });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, credsRes] = await Promise.all([
          axios.get(`${API}/admin/settings`, { headers: getHeaders(), withCredentials: true }),
          axios.get(`${API}/admin/api-credentials`, { headers: getHeaders(), withCredentials: true })
        ]);
        setSettings(settingsRes.data);
        
        // Set credentials (masked values from backend)
        const maskedCreds = credsRes.data;
        setCredentials(prev => ({
          ...prev,
          ...Object.fromEntries(
            Object.entries(maskedCreds).filter(([k]) => k !== 'credential_id')
          )
        }));
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
      setLoading(false);
    };

    fetchData();
  }, [token]);

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, settings, {
        headers: getHeaders(),
        withCredentials: true
      });
      toast.success('Website settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    }
    setSaving(false);
  };

  const handleSaveCredentials = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    // Only send non-masked values (values that don't contain ****)
    const cleanCredentials = {};
    Object.entries(credentials).forEach(([key, value]) => {
      if (value && !value.includes('****')) {
        cleanCredentials[key] = value;
      }
    });
    
    if (Object.keys(cleanCredentials).length === 0) {
      toast.info('No changes to save');
      setSaving(false);
      return;
    }

    try {
      await axios.put(`${API}/admin/api-credentials`, cleanCredentials, {
        headers: getHeaders(),
        withCredentials: true
      });
      toast.success('API credentials saved');
      
      // Refresh to get masked values
      const credsRes = await axios.get(`${API}/admin/api-credentials`, {
        headers: getHeaders(),
        withCredentials: true
      });
      setCredentials(prev => ({
        ...prev,
        ...Object.fromEntries(
          Object.entries(credsRes.data).filter(([k]) => k !== 'credential_id')
        )
      }));
    } catch (error) {
      toast.error('Failed to save credentials');
    }
    setSaving(false);
  };

  const toggleShowSecret = (field) => {
    setShowSecrets(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const renderSecretInput = (field, label, placeholder) => (
    <div className="space-y-2">
      <Label htmlFor={field}>{label}</Label>
      <div className="relative">
        <Input
          id={field}
          type={showSecrets[field] ? 'text' : 'password'}
          value={credentials[field] || ''}
          onChange={(e) => setCredentials({ ...credentials, [field]: e.target.value })}
          placeholder={placeholder}
          className="pr-10"
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-0 top-0 h-full"
          onClick={() => toggleShowSecret(field)}
        >
          {showSecrets[field] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="admin-settings">
      <h1 className="font-heading text-3xl font-bold mb-8">Settings</h1>

      <Tabs defaultValue="website" className="space-y-6">
        <TabsList>
          <TabsTrigger value="website" className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            Website
          </TabsTrigger>
          <TabsTrigger value="api" className="flex items-center gap-2">
            <Key className="h-4 w-4" />
            API Credentials
          </TabsTrigger>
        </TabsList>

        {/* Website Settings Tab */}
        <TabsContent value="website">
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Website Settings
              </CardTitle>
              <CardDescription>Configure your store's public information</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveSettings} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="site_name">Site Name</Label>
                    <Input
                      id="site_name"
                      value={settings.site_name || ''}
                      onChange={(e) => setSettings({ ...settings, site_name: e.target.value })}
                      placeholder="Dior Russell Books"
                      data-testid="settings-site-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tagline">Tagline</Label>
                    <Input
                      id="tagline"
                      value={settings.tagline || ''}
                      onChange={(e) => setSettings({ ...settings, tagline: e.target.value })}
                      placeholder="A Digital Sanctuary for Books"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="hero_title">Hero Title</Label>
                  <Input
                    id="hero_title"
                    value={settings.hero_title || ''}
                    onChange={(e) => setSettings({ ...settings, hero_title: e.target.value })}
                    placeholder="Discover Your Next Great Read"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="hero_subtitle">Hero Subtitle</Label>
                  <Input
                    id="hero_subtitle"
                    value={settings.hero_subtitle || ''}
                    onChange={(e) => setSettings({ ...settings, hero_subtitle: e.target.value })}
                    placeholder="Curated collections for the modern reader"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact_email">Contact Email</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={settings.contact_email || ''}
                    onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                    placeholder="contact@diorrussell.com"
                  />
                </div>

                <Button type="submit" className="btn-lift" disabled={saving}>
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  <Save className="h-4 w-4 mr-2" />
                  Save Settings
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* API Credentials Tab */}
        <TabsContent value="api">
          <form onSubmit={handleSaveCredentials} className="space-y-6">
            {/* PayPal */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="font-heading text-lg">PayPal</CardTitle>
                <CardDescription>Configure PayPal payment integration</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  {renderSecretInput('paypal_client_id', 'Client ID', 'PayPal Client ID')}
                  {renderSecretInput('paypal_secret', 'Client Secret', 'PayPal Client Secret')}
                </div>
              </CardContent>
            </Card>

            {/* SendGrid */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="font-heading text-lg">SendGrid</CardTitle>
                <CardDescription>Configure email service for newsletters</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  {renderSecretInput('sendgrid_api_key', 'API Key', 'SG.xxxxxx')}
                  <div className="space-y-2">
                    <Label htmlFor="sendgrid_sender_email">Sender Email</Label>
                    <Input
                      id="sendgrid_sender_email"
                      type="email"
                      value={credentials.sendgrid_sender_email || ''}
                      onChange={(e) => setCredentials({ ...credentials, sendgrid_sender_email: e.target.value })}
                      placeholder="noreply@yourstore.com"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Twitter */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Twitter / X</CardTitle>
                <CardDescription>Configure Twitter API for social posting</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  {renderSecretInput('twitter_api_key', 'API Key', 'Twitter API Key')}
                  {renderSecretInput('twitter_api_secret', 'API Secret', 'Twitter API Secret')}
                  {renderSecretInput('twitter_access_token', 'Access Token', 'Twitter Access Token')}
                  {renderSecretInput('twitter_access_secret', 'Access Token Secret', 'Twitter Access Secret')}
                </div>
              </CardContent>
            </Card>

            {/* Facebook */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Facebook</CardTitle>
                <CardDescription>Configure Facebook API for social posting</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  {renderSecretInput('facebook_app_id', 'App ID', 'Facebook App ID')}
                  {renderSecretInput('facebook_app_secret', 'App Secret', 'Facebook App Secret')}
                </div>
                {renderSecretInput('facebook_page_token', 'Page Access Token', 'Facebook Page Token')}
              </CardContent>
            </Card>

            {/* Instagram */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Instagram</CardTitle>
                <CardDescription>Configure Instagram API for social posting</CardDescription>
              </CardHeader>
              <CardContent>
                {renderSecretInput('instagram_access_token', 'Access Token', 'Instagram Access Token')}
              </CardContent>
            </Card>

            {/* TikTok */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="font-heading text-lg">TikTok</CardTitle>
                <CardDescription>Configure TikTok API for social posting</CardDescription>
              </CardHeader>
              <CardContent>
                {renderSecretInput('tiktok_access_token', 'Access Token', 'TikTok Access Token')}
              </CardContent>
            </Card>

            {/* WhatsApp */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  WhatsApp Business
                </CardTitle>
                <CardDescription>Configure WhatsApp Business API for messaging</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  {renderSecretInput('whatsapp_phone_id', 'Phone Number ID', 'WhatsApp Phone ID')}
                  {renderSecretInput('whatsapp_access_token', 'Access Token', 'WhatsApp Access Token')}
                </div>
              </CardContent>
            </Card>

            <Button type="submit" className="btn-lift" disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Save className="h-4 w-4 mr-2" />
              Save API Credentials
            </Button>
          </form>
        </TabsContent>
      </Tabs>
    </div>
  );
};
